// Pure logic for the Passing widget (Players canvas — the last "#5" widget).
// The widget is a thin view; the maths that's worth testing lives here. Grows
// tile-by-tile: Summary first (this file), then peer percentiles + style shares.
//
// Data-scale note (verified live against the engine, EPL 52025):
//   pass_success_perc / expected_pass_success_perc come back as 0–1 FRACTIONS
//   (0.663, 0.741), so they're ×100 to read as points. avg_pass_difficulty and
//   psax_90 are plain decimals. See docs/frontend-canvas.md.

/** Read a numeric cell, tolerating strings/nulls. null when absent/NaN. */
export function num(
  row: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  const v = row?.[key];
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export interface PassCompletion {
  /** Actual completion, in points (0–100). */
  actualPct: number | null;
  /** xPass-model expected completion, in points (0–100). */
  expectedPct: number | null;
  /** actual − expected, in points. Positive = beat the model. */
  gapPts: number | null;
}

/**
 * The headline "is he a good passer?" number: actual completion vs what the
 * xPass model expected given the difficulty of the passes tried, and the gap.
 * Inputs are 0–1 fractions in the data; we return points (×100).
 */
export function passCompletion(
  row: Record<string, unknown> | null | undefined,
): PassCompletion {
  const actual = num(row, "pass_success_perc");
  const expected = num(row, "expected_pass_success_perc");
  return {
    actualPct: actual === null ? null : actual * 100,
    expectedPct: expected === null ? null : expected * 100,
    gapPts: actual === null || expected === null ? null : (actual - expected) * 100,
  };
}

export type StatsRow = Record<string, unknown>;

/**
 * The peer rows that count: those with enough minutes. The floor is applied
 * here (client-side) so tuning it never refetches. A "positional" peer set is
 * already position-filtered by the query; this just drops low-minute noise.
 * Matches old Catalyst's default 150-minute floor (verified: MLS 2025 strikers
 * → 135 peers, the same count the old app shows).
 */
export function flooredPeers(rows: StatsRow[], minutesFloor: number): StatsRow[] {
  return rows.filter((r) => (num(r, "minutes") ?? 0) >= minutesFloor);
}

/**
 * The peer UNWEIGHTED mean row: the simple mean of each key across the floored
 * peers — every player counts once, regardless of minutes. This is one of the
 * three comparison bases old Catalyst offers; the DEFAULT (and what the widgets
 * currently show) is instead the volume-weighted league season average
 * (`usePlayerStatsGroupAvg`). This unweighted mean is wired in when the
 * comparison drawer lands (it also offers the median). A key with no numeric
 * values in any peer is omitted (so callers read null, not NaN).
 */
export function peerMeanRow(
  rows: StatsRow[],
  keys: string[],
  minutesFloor: number,
): { row: StatsRow; count: number } {
  const peers = flooredPeers(rows, minutesFloor);
  const row: StatsRow = {};
  for (const key of keys) {
    const vals = peers.map((r) => num(r, key)).filter((v): v is number => v !== null);
    if (vals.length > 0) row[key] = vals.reduce((a, b) => a + b, 0) / vals.length;
  }
  return { row, count: peers.length };
}

/**
 * The player's percentile within a peer distribution, as 0–100: the share of
 * peers at or below `value`. So 90 = "better than ~90% of the group" (for a
 * higher-is-better metric). null when there are no peer values to rank against.
 * Rank-based, so it's robust to the wild per-90 outliers a low-minute fringe
 * player can produce.
 */
export function percentileOf(value: number, peerValues: number[]): number | null {
  if (peerValues.length === 0) return null;
  const atOrBelow = peerValues.filter((v) => v <= value).length;
  return (100 * atOrBelow) / peerValues.length;
}

/** The numeric values of one key across rows, dropping nulls. Feeds percentileOf. */
export function peerValues(rows: StatsRow[], key: string): number[] {
  return rows.map((r) => num(r, key)).filter((v): v is number => v !== null);
}

/**
 * Which of the 7 diverging success bands a percentile falls in: 0 = weakest,
 * 3 = neutral (median), 6 = strongest. Approximates old Catalyst's
 * `successLevelForPercentile` (its "bands (<10 … ≥90)") so the percentile bars
 * colour the same warm→grey→cool way. The widget maps the index to a colour.
 */
export function percentileBand(pct: number): number {
  if (pct < 10) return 0;
  if (pct < 30) return 1;
  if (pct < 45) return 2;
  if (pct < 55) return 3;
  if (pct < 70) return 4;
  if (pct < 90) return 5;
  return 6;
}

export interface DifficultyBand {
  id: "hard" | "medium" | "easy";
  label: string;
  attempted: number | null;
  completed: number | null;
  /** Model-expected completions = completed − PSAX (the per-band identity). */
  expected: number | null;
  /** Pass Success Above Expectation (total) — completed minus expected. */
  psax: number | null;
  /** Actual completion share of attempts (0–1). */
  successPerc: number | null;
  /** Model-expected completion share = expected ÷ attempted (0–1). */
  expectedPerc: number | null;
}

const BAND_DEFS: { id: DifficultyBand["id"]; label: string }[] = [
  { id: "hard", label: "Hard" },
  { id: "medium", label: "Medium" },
  { id: "easy", label: "Easy" },
];

/**
 * Per-difficulty-band passing figures (hard/medium/easy) for the Difficulty
 * Bands widget. `expected` isn't a stored metric — it follows from the identity
 * completed = expected + PSAX, so expected = completed − psax (verified against
 * old Catalyst). Totals basis (no per-90). Any missing input yields null, not NaN.
 */
export function buildBands(row: StatsRow | null | undefined): DifficultyBand[] {
  return BAND_DEFS.map(({ id, label }) => {
    const attempted = num(row, `passes_${id}`);
    const completed = num(row, `passes_${id}_successful`);
    const psax = num(row, `psax_${id}`);
    const expected = completed !== null && psax !== null ? completed - psax : null;
    const successPerc = num(row, `${id}_pass_success_perc`);
    const expectedPerc = expected !== null && attempted ? expected / attempted : null;
    return { id, label, attempted, completed, expected, psax, successPerc, expectedPerc };
  });
}

export interface ShareSegment {
  key: string;
  /** The raw share as a fraction 0–1 (the data's native scale). */
  value: number;
  /** Segment width as a % of the bar (share ÷ total ×100) — always fills the bar. */
  widthPct: number;
}

/**
 * Turn a set of share keys (fractions that sum to ~1, e.g. hard/medium/easy pass
 * shares) into stacked-bar segments. Widths are normalised to the total so the
 * bar always fills even when the shares sum to slightly under 1 (the data can be
 * 0.997 from rounding / an uncategorised sliver); the raw `value` is kept for the
 * label. A key with no value contributes 0. Returns [] if nothing is present.
 */
export function stackedShares(
  row: StatsRow | null | undefined,
  keys: string[],
): ShareSegment[] {
  const raw = keys.map((key) => ({ key, value: num(row, key) ?? 0 }));
  const total = raw.reduce((a, b) => a + b.value, 0);
  if (total <= 0) return [];
  return raw.map(({ key, value }) => ({ key, value, widthPct: (100 * value) / total }));
}
